import { useEffect, useState } from "react";

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/**
 * Returns false on first render, then true once the browser is idle
 * (or after a short fallback). Gate non-critical React Query calls on
 * this via their `enabled` option so they fire just AFTER the page's
 * own critical data instead of competing with it in the initial
 * request burst. Fewer concurrent requests on the critical path means
 * fewer Lambda containers a page load needs warm at once.
 */
export const useDeferredReady = (timeoutMs = 1500): boolean => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as IdleWindow;
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setReady(true), {
        timeout: timeoutMs,
      });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(() => setReady(true), 200);
    return () => window.clearTimeout(t);
  }, [timeoutMs]);
  return ready;
};
